import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, X } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { Section } from "./_shared";

export default function RequestsPanel({ brand }) {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/brands/${brand.id}/requests`);
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.requests_load_failed"));
      setRows([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [brand.id]);

  const decide = async (row, decision) => {
    setBusyId(row.id);
    try {
      await api.post(`/brands/${brand.id}/requests/${row.type}/${row.id}/decide`, { decision });
      toast.success(decision === "approve" ? t("settings.requests_approved") : t("settings.requests_denied"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.requests_action_failed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section
      title={t("settings.requests_title")}
      subtitle={t("settings.requests_sub")}
      action={null}
    >
      {rows === null ? (
        <div className="text-sm text-gray-500 py-6">{t("settings.requests_loading")}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 py-14 text-center" data-testid="requests-empty">
          <Clock className="w-5 h-5 mx-auto text-gray-400 mb-2" strokeWidth={1.75} />
          <div className="text-sm text-gray-500">{t("settings.requests_empty")}</div>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 bg-white border border-gray-100 rounded-xl" data-testid="requests-list">
          {rows.map((r) => {
            const who = r.requested_by_name || r.requested_by_email || "—";
            const roleLabel = (r.requested_role || "").charAt(0).toUpperCase() + (r.requested_role || "").slice(1);
            const actionTitle = r.type === "ai_access"
              ? t("settings.requests_type_ai")
              : t("settings.requests_type_role");
            const sentence = (r.type === "ai_access"
              ? t("settings.requests_sentence_ai")
              : t("settings.requests_sentence_role")
            ).replace("{name}", who).replace("{role}", roleLabel);
            return (
            <div key={r.id} className="flex items-center gap-3 py-3 px-4" data-testid={`request-row-${r.id}`}>
              {r.picture ? (
                <img src={r.picture.startsWith("/api") ? `${API.replace(/\/api$/, "")}${r.picture}` : r.picture} alt="" className="w-9 h-9 rounded-md object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-md bg-gray-200 flex items-center justify-center text-sm text-slate-700">
                  {who.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-slate-900">{actionTitle}</div>
                <div className="text-xs text-gray-500 truncate">
                  {sentence}
                  {r.note ? ` — ${r.note}` : ""}
                </div>
              </div>
              <Button
                onClick={() => decide(r, "deny")}
                disabled={busyId === r.id}
                data-testid={`request-deny-${r.id}`}
                className="h-9 rounded-md bg-white border border-gray-200 text-slate-700 hover:bg-gray-50 shadow-none text-sm"
              >
                <X className="w-4 h-4 mr-1.5" /> {t("settings.requests_deny")}
              </Button>
              <Button
                onClick={() => decide(r, "approve")}
                disabled={busyId === r.id}
                data-testid={`request-approve-${r.id}`}
                className="h-9 rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm"
              >
                <Check className="w-4 h-4 mr-1.5" /> {t("settings.requests_approve")}
              </Button>
            </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
